import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useParams,Link } from 'react-router-dom';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import logo from './logo.png';

import { FaTimes, FaBars, FaDownload, FaShare, FaCrop, FaCheck, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";
import './viewer.css';

const BaseUrl = "https://localhost:44351/"
const BaseUrl_online = "https://api.shabdtoday.com/"
const local = "https://localhost:44351/api/home/getPdfViewer/"
const online = "https://api.shabdtoday.com/api/home/getPdfViewer/"

const Viewer = () => {
    const { id } = useParams();
    const location = useLocation();
    const [totalPages, settotalPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [cropMode, setCropMode] = useState(false);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [showShareOptions, setShowShareOptions] = useState(false);
    const [pdfImage, setPdfImage] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const [pdfHeading, setPdfHeading] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const pdfCanvasRef = useRef(null);

    useEffect(() => {
        pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

        const fetchpdfFile = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await fetch(`${online}${id}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    mode: 'cors',
                    credentials: 'include'
                });


                if (!response.ok) {
                    throw new Error('Failed to fetch PDF');
                }

                const data = await response.json();
                // console.log(data)
                setPdfFile(data.pdfUrl);
                setPdfHeading(data.title);
            } catch (err) {
                console.error('Error fetching PDF:', err);
                setError(err.message || 'Failed to load PDF');
            } finally {
                setIsLoading(false);
            }
        };

        fetchpdfFile();

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            if (window.innerWidth > 768) {
                setIsSidebarOpen(true);
            } else {
                setIsSidebarOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);

        if (window.innerWidth > 768) {
            setIsSidebarOpen(true);
        }

        return () => window.removeEventListener('resize', handleResize);
    }, [id]);

    useEffect(() => {
        if (completedCrop && imgRef.current && canvasRef.current) {
            const image = imgRef.current;
            const canvas = canvasRef.current;
            const crop = completedCrop;

            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;
            const ctx = canvas.getContext('2d');
            const pixelRatio = window.devicePixelRatio;

            canvas.width = crop.width * pixelRatio;
            canvas.height = crop.height * pixelRatio;

            ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
            ctx.imageSmoothingQuality = 'high';

            ctx.drawImage(
                image,
                crop.x * scaleX,
                crop.y * scaleY,
                crop.width * scaleX,
                crop.height * scaleY,
                0,
                0,
                crop.width,
                crop.height
            );

            try {
                const dataUrl = canvas.toDataURL('image/jpeg', 1.0);
                setCroppedImage(dataUrl);
            } catch (error) {
                console.error('Error creating cropped image:', error);
                alert('Failed to create cropped image');
            }
        }
    }, [completedCrop]);

    useEffect(() => {
        if (cropMode && pdfCanvasRef.current) {
            try {
                const canvas = pdfCanvasRef.current;
                const image = new Image();
                image.src = canvas.toDataURL('image/jpeg');
                image.onload = () => {
                    setPdfImage(image.src);
                };
                image.onerror = () => {
                    console.error('Failed to load PDF as image');
                };
            } catch (error) {
                console.error('Error converting PDF to image:', error);
            }
        }
    }, [pageNumber, cropMode]);

    useEffect(() => {
        if (!pdfFile) return;

        const loadPdf = async () => {
            try {
                const pdf = await pdfjs.getDocument(pdfFile).promise;
                settotalPages(pdf.numPages);
            } catch (err) {
                console.error("Error loading PDF:", err);
            }
        };

        loadPdf();
    }, [pdfFile]);

    if (isLoading) {
        return (
            <div className="viewer-loading">
                <FaSpinner className="spinner" />
                <p>Loading PDF...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="viewer-error">
                <p>Error: {error}</p>
            </div>
        );
    }

    if (!pdfFile) {
        return (
            <div className="viewer-error">
                <p>No PDF file available</p>
            </div>
        );
    }

    function onDocLoad({ numPages }) {
        settotalPages(numPages);
    }


    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleCropComplete = (crop) => {
        setCompletedCrop(crop);
    };

    const handleSaveCrop = () => {
        setCropMode(false);
    };

    const handleCancelCrop = () => {
        setCropMode(false);
        setCroppedImage(null);
    };

    const handleShare = async () => {
        if (!croppedImage) {
            alert('Please crop an image first');
            return;
        }

        try {
            if (navigator.share) {
                const response = await fetch(croppedImage);
                const blob = await response.blob();

                await navigator.share({
                    title: 'Cropped PDF Image',
                    text: 'Check out this cropped image from my PDF',
                    files: [new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' })]
                });
            } else {
                setShowShareOptions(true);
            }
        } catch (err) {
            console.error('Error sharing:', err);
            setShowShareOptions(true);
        }
    };

    const shareToSocialMedia = (platform) => {
        let shareUrl = '';
        const text = 'Check out this cropped image from my PDF';

        switch (platform) {
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(croppedImage)}`;
                break;
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(croppedImage)}`;
                break;
            case 'linkedin':
                shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(croppedImage)}&title=${encodeURIComponent(text)}`;
                break;
            default:
                return;
        }

        window.open(shareUrl, '_blank', 'width=600,height=400');
        setShowShareOptions(false);
    };

    const changePage = (param) => {
        setPageNumber((prev) => {
            if (param === "prev" && prev > 1) {
                return prev - 1;
            }
            if (param === "next" && prev < totalPages) {
                return prev + 1;
            }
            return prev;
        });
    };

    return (
        <div className="viewer-container">

            {isSidebarOpen && (
                <div className="viewer-sidebar">
                    <div className="viewer-header">
                        <Link to="/"><img src={logo} alt="Logo" style={{ maxWidth: 120 }} /></Link>
                        {windowWidth <= 768 && (
                            <button onClick={toggleSidebar} className="viewer-close-sidebar">
                                <FaTimes />
                            </button>
                        )}
                    </div>
                    <div className="viewer-full-height">
                        <Document
                            className="viewer-custom-container"
                            file={pdfFile}
                            onLoadSuccess={onDocLoad}
                        >
                            {Array(totalPages)
                                .fill()
                                .map((_, index) => (
                                    <div
                                        key={index}
                                        onClick={() => {
                                            setPageNumber(index + 1);
                                            if (windowWidth <= 768) {
                                                setIsSidebarOpen(false);
                                            }
                                        }}
                                        className={`viewer-custom-button ${pageNumber === index + 1 ? 'border-black' : ''}`}
                                    >
                                        <Page
                                            height={180}
                                            pageNumber={index + 1}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                        />
                                    </div>
                                ))
                            }
                        </Document>
                    </div>
                </div>
            )}

            {windowWidth <= 768 && (
                <div className="viewer-mobile-header">
                    <button onClick={toggleSidebar} className="viewer-sidebar-toggle">
                        {isSidebarOpen ? <FaTimes /> : <FaBars />}
                    </button>
                    <div className='viewer-custom-text'>
                        {pdfHeading ? pdfHeading : ''}
                    </div>
                    <div className="viewer-mobile-actions">
                        {cropMode ? (
                            <>
                                <button onClick={handleSaveCrop} className="viewer-action-button">
                                    <FaCheck />
                                </button>
                                <button onClick={handleCancelCrop} className="viewer-action-button">
                                    <FaTimesCircle />
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={() => setCropMode(true)} className="viewer-action-button">
                                    <FaCrop />
                                </button>
                                <button onClick={handleShare} className="viewer-action-button">
                                    <FaShare />
                                </button>
                                <a href={pdfFile} target="_blank" className='viewer-button-style' rel="noreferrer">
                                    <FaDownload />
                                </a>

                            </>
                        )}
                    </div>
                    <div className='viewer-centered-flex'>
                        <IoIosArrowBack
                            className='cursor-pointer'
                            onClick={() => changePage("prev")}
                        />
                        <div className='px-3 py-1 rounded'>{pageNumber}</div>
                        of
                        <div className='px-3 py-1 rounded'>{totalPages}</div>
                        <IoIosArrowForward
                            className='cursor-pointer'
                            onClick={() => changePage("next")}
                        />
                    </div>
                </div>

            )}

            <div className='viewer-full-width viewer-full-height'>
                <div className='viewer-full-width-bg'>
                    {windowWidth > 768 && (
                        <div className='viewer-header-bar'>
                            <div className='viewer-custom-text'>
                                {pdfHeading ? pdfHeading : ''}
                            </div>
                            <div className='viewer-centered-flex'>
                                {cropMode ? (
                                    <>
                                        <button onClick={handleSaveCrop} className="viewer-action-button">
                                            <FaCheck /> Save Crop
                                        </button>
                                        <button onClick={handleCancelCrop} className="viewer-action-button">
                                            <FaTimesCircle /> Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => setCropMode(true)} className="viewer-action-button">
                                            <FaCrop /> Crop
                                        </button>
                                        <button onClick={handleShare} className="viewer-action-button">
                                            <FaShare /> Share
                                        </button>
                                    </>
                                )}
                                <div className='viewer-centered-flex'>
                                    <IoIosArrowBack
                                        className='cursor-pointer'
                                        onClick={() => changePage("prev")}
                                    />
                                    <div className='px-3 py-1 rounded'>{pageNumber}</div>
                                    of
                                    <div className='px-3 py-1 rounded'>{totalPages}</div>
                                    <IoIosArrowForward
                                        className='cursor-pointer'
                                        onClick={() => changePage("next")}
                                    />
                                </div>
                            </div>
                            <div>
                                <a href={pdfFile} target="_blank" className='viewer-button-style' rel="noreferrer">
                                    <FaDownload /> Download
                                </a>
                            </div>
                        </div>
                    )}
                    <div className='viewer-full-container' style={{ transform: `scale(1)`, transformOrigin: 'center' }}>
                        <div style={{ display: 'none' }}>
                            <Document file={pdfFile}>
                                <Page
                                    pageNumber={pageNumber}
                                    width={windowWidth <= 768 ? windowWidth - 32 : undefined}
                                    canvasRef={pdfCanvasRef}
                                />
                            </Document>
                        </div>

                        {cropMode ? (
                            <div className="viewer-crop-container">
                                {pdfImage && (
                                    <ReactCrop
                                        crop={crop}
                                        onChange={c => setCrop(c)}
                                        onComplete={handleCropComplete}
                                    >
                                        <img
                                            ref={imgRef}
                                            src={pdfImage}
                                            alt="PDF Page"
                                            style={{ maxWidth: '100%' }}
                                            crossOrigin="anonymous"
                                        />
                                    </ReactCrop>
                                )}
                                <canvas
                                    ref={canvasRef}
                                    style={{
                                        display: 'none',
                                        border: '1px solid black',
                                        objectFit: 'contain',
                                    }}
                                />
                            </div>
                        ) : croppedImage ? (
                            <div className="viewer-cropped-image-container">
                                <img src={croppedImage} alt="Cropped" className="viewer-cropped-image" />
                            </div>
                        ) : (
                            <Document file={pdfFile}>
                                <Page
                                    pageNumber={pageNumber}
                                    width={windowWidth <= 768 ? windowWidth - 32 : undefined}
                                />
                            </Document>
                        )}
                    </div>
                </div>
            </div>

            {showShareOptions && (
                <div className="viewer-share-modal">
                    <div className="viewer-share-modal-content">
                        <h3>Share to</h3>
                        <div className="viewer-share-buttons">
                            <button onClick={() => shareToSocialMedia('facebook')}>Facebook</button>
                            <button onClick={() => shareToSocialMedia('twitter')}>Twitter</button>
                            <button onClick={() => shareToSocialMedia('linkedin')}>LinkedIn</button>
                            <button onClick={() => {
                                navigator.clipboard.writeText(croppedImage)
                                    .then(() => alert('Image link copied to clipboard!'))
                                    .catch(err => console.error('Could not copy text: ', err));
                                setShowShareOptions(false);
                            }}>Copy Link</button>
                        </div>
                        <button
                            onClick={() => setShowShareOptions(false)}
                            className="viewer-close-share-modal"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Viewer;